/**
 * geoBoundaries gbOpen GeoJSON: hosted JSON/GeoJSON on CDN + no runtime geoBoundaries API.
 * Default base: https://file.useopenwrench.com/spatial_data (manifest.json, WORLD-ADM0.geojson, {ISO}-ADM1/2.geojson).
 * Override for local files: REACT_APP_GEO_BOUNDARIES_BASE_URL=/geo/geoBoundaries/ (yarn geoBoundaries:download-local).
 * License: CC BY 4.0 — attribute https://www.geoboundaries.org/ in the UI.
 */

const DEFAULT_GEO_BOUNDARIES_BASE_URL = 'https://file.useopenwrench.com/spatial_data/';

function geoBoundariesBaseUrl(): string {
	const raw = process.env.REACT_APP_GEO_BOUNDARIES_BASE_URL;
	if (raw != null && String(raw).trim() !== '') {
		let u = String(raw).trim();
		const last = u.charAt(u.length - 1);
		if (last !== '/') {
			u = u + '/';
		}
		return u;
	}
	return DEFAULT_GEO_BOUNDARIES_BASE_URL;
}

export function localGeoBoundariesAssetPath(iso: string, boundaryType: string): string {
	return geoBoundariesBaseUrl() + iso + '-' + boundaryType + '.geojson';
}

/** Merged country outlines for the whole dataset (one request at world zoom). */
export function worldAdm0AssetPath(): string {
	return geoBoundariesBaseUrl() + 'WORLD-ADM0.geojson';
}

export function geoBoundariesManifestPath(): string {
	return geoBoundariesBaseUrl() + 'manifest.json';
}

/**
 * Whether we attempt to load ADM1/ADM2 for this ISO. Matches geoBoundaries gbOpen folder codes (ISO 3166-1 alpha-3).
 */
export function hasLocalGeoBoundariesData(iso: string): boolean {
	if (!iso || typeof iso !== 'string') {
		return false;
	}
	const s = iso.trim().toUpperCase();
	if (s.length !== 3) {
		return false;
	}
	for (let i = 0; i < 3; i++) {
		const c = s.charCodeAt(i);
		if (c < 65 || c > 90) {
			return false;
		}
	}
	return true;
}

export interface GeoBoundariesManifest {
	isos: string[];
	generatedAt?: string;
	levels?: string[];
	source?: string;
}

export async function fetchGeoBoundariesManifest(): Promise<GeoBoundariesManifest | null> {
	try {
		const res = await fetch(geoBoundariesManifestPath());
		if (!res.ok) {
			return null;
		}
		return res.json();
	} catch (_e) {
		return null;
	}
}

export async function fetchWorldAdm0GeoJson(): Promise<any> {
	const url = worldAdm0AssetPath();
	const res = await fetch(url);
	if (!res.ok) {
		throw new Error('WORLD-ADM0 GeoJSON HTTP ' + res.status + ' ' + url);
	}
	return res.json();
}

export async function fetchLocalGeoBoundariesGeoJson(
	iso: string,
	boundaryType: string
): Promise<any> {
	const url = localGeoBoundariesAssetPath(iso, boundaryType);
	const res = await fetch(url);
	if (!res.ok) {
		throw new Error('Local GeoJSON HTTP ' + res.status + ' ' + url);
	}
	return res.json();
}

/** Simple in-memory cache to avoid re-fetching the same CDN file in one session. */
const _cdnCache: Record<string, any> = {};
const WORLD_ADM0_CACHE_KEY = 'WORLD-ADM0';

// Fetch-once accessor for per-ISO geoBoundaries GeoJSON; shares `_cdnCache` with resolveServiceAreaBoundariesFromCDN. Returns null on failure.
export async function ensureCachedGeoBoundariesFeatureCollection(
	iso: string,
	boundaryType: 'ADM0' | 'ADM1' | 'ADM2' | 'ADM3'
): Promise<any | null> {
	const countryIso = String(iso || '').toUpperCase();
	if (!countryIso || !hasLocalGeoBoundariesData(countryIso)) {
		return null;
	}
	const fileKey = countryIso + '-' + boundaryType;
	if (_cdnCache[fileKey]) {
		return _cdnCache[fileKey];
	}
	try {
		const featureCollection = await fetchLocalGeoBoundariesGeoJson(countryIso, boundaryType);
		_cdnCache[fileKey] = featureCollection;
		return featureCollection;
	} catch (_e) {
		return null;
	}
}

/** Fetch-once accessor for the merged world ADM0 file (used to map ISO codes to country labels). */
export async function ensureCachedWorldAdm0FeatureCollection(): Promise<any | null> {
	if (_cdnCache[WORLD_ADM0_CACHE_KEY]) {
		return _cdnCache[WORLD_ADM0_CACHE_KEY];
	}
	try {
		const featureCollection = await fetchWorldAdm0GeoJson();
		_cdnCache[WORLD_ADM0_CACHE_KEY] = featureCollection;
		return featureCollection;
	} catch (_e) {
		return null;
	}
}

function admLevelFromPlaceType(placeType: string): string {
	if (placeType === 'country') return 'ADM0';
	if (placeType === 'region') return 'ADM1';
	if (placeType === 'district') return 'ADM2';
	if (placeType === 'place') return 'ADM3';
	return 'ADM1';
}

/**
 * Resolves boundary geometries for service areas that have `boundaryShapeId` + `boundaryShapeGroup`
 * but no `boundaries` yet. Fetches the matching CDN GeoJSON and extracts the feature geometry.
 * Returns a new array with `boundaries` populated on each resolved area.
 */
export async function resolveServiceAreaBoundariesFromCDN(areas: any[]): Promise<any[]> {
	if (!areas || areas.length === 0) return areas;

	// Group areas by CDN file key (iso + adm level)
	const fileGroups: Record<string, { iso: string; adm: string; indices: number[] }> = {};
	for (let areaIndex = 0; areaIndex < areas.length; areaIndex++) {
		const area = areas[areaIndex];
		if (area.boundaries || !area.boundaryShapeId || !area.boundaryShapeGroup) continue;
		const countryIso = String(area.boundaryShapeGroup).toUpperCase();
		const admLevel = admLevelFromPlaceType(area.placeType || 'region');
		const fileKey = countryIso + '-' + admLevel;
		if (!fileGroups[fileKey]) {
			fileGroups[fileKey] = { iso: countryIso, adm: admLevel, indices: [] };
		}
		fileGroups[fileKey].indices.push(areaIndex);
	}

	const fileKeys = Object.keys(fileGroups);
	if (fileKeys.length === 0) return areas;

	// Fetch each needed file (with cache)
	const fetchPromises = fileKeys.map((fileKey) => {
		const group = fileGroups[fileKey];
		if (_cdnCache[fileKey]) {
			return Promise.resolve({ fileKey: fileKey, featureCollection: _cdnCache[fileKey] });
		}
		// For ADM0, prefer shared world cache/file; fallback to per-country ADM0 file.
		if (group.adm === 'ADM0' && _cdnCache[WORLD_ADM0_CACHE_KEY]) {
			const sharedFeatureCollection = _cdnCache[WORLD_ADM0_CACHE_KEY];
			_cdnCache[fileKey] = sharedFeatureCollection;
			return Promise.resolve({ fileKey: fileKey, featureCollection: sharedFeatureCollection });
		}
		const fetchFn =
			group.adm === 'ADM0'
				? fetchWorldAdm0GeoJson()
						.then((featureCollection) => {
							_cdnCache[WORLD_ADM0_CACHE_KEY] = featureCollection;
							_cdnCache[fileKey] = featureCollection;
							return featureCollection;
						})
						.catch(() => fetchLocalGeoBoundariesGeoJson(group.iso, group.adm))
				: fetchLocalGeoBoundariesGeoJson(group.iso, group.adm);

		return fetchFn
			.then((featureCollection) => {
				_cdnCache[fileKey] = featureCollection;
				return { fileKey: fileKey, featureCollection: featureCollection };
			})
			.catch(() => ({ fileKey: fileKey, featureCollection: null }));
	});

	const results = await Promise.all(fetchPromises);

	// Build shapeID → geometry lookup and resolve
	for (let resultIndex = 0; resultIndex < results.length; resultIndex++) {
		const result = results[resultIndex];
		if (!result.featureCollection || !result.featureCollection.features) continue;
		const group = fileGroups[result.fileKey];
		const features = result.featureCollection.features;

		// Build lookup for this file
		const shapeIdToGeometry: Record<string, any> = {};
		for (let featureIndex = 0; featureIndex < features.length; featureIndex++) {
			const feature = features[featureIndex];
			if (feature && feature.properties && feature.properties.shapeID) {
				shapeIdToGeometry[String(feature.properties.shapeID)] = feature.geometry;
			}
		}

		// Resolve each area
		for (let groupIndex = 0; groupIndex < group.indices.length; groupIndex++) {
			const targetIndex = group.indices[groupIndex];
			const shapeId = String(areas[targetIndex].boundaryShapeId);
			if (shapeIdToGeometry[shapeId]) {
				areas[targetIndex] = Object.assign({}, areas[targetIndex], {
					boundaries: shapeIdToGeometry[shapeId],
				});
			}
		}
	}

	return areas;
}
